import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { walletAPI, ordersAPI, usersAPI, storesAPI, storeRequestsAPI } from '../lib/api';
import { toast } from 'react-hot-toast';
import { Truck, Loader2, MapPin, CheckCircle, DollarSign, ShoppingCart, Navigation, Camera, ArrowRight, Store, Plus } from 'lucide-react';
import TravelTimeMap from '../components/Map/TravelTimeMap';
import { getRoute, geocode } from '../lib/traveltime';

const TRAVELTIME_APP_ID = import.meta.env.VITE_TRAVELTIME_APP_ID;
const TRAVELTIME_API_KEY = import.meta.env.VITE_TRAVELTIME_API_KEY;

interface Delivery {
    id: string;
    orderId: string;
    customer: string;
    phone: string;
    address: string;
    items: Array<{ name: string; quantity: number; price: number }>;
    totalAmount: number;
    status: string;
    distance?: string;
    eta?: string;
    location?: { lat: number; lng: number };
    storeLocation?: { lat: number; lng: number };
}

const DeliveryAgentDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<any>(null);
    const [walletBalance, setWalletBalance] = useState(0);
    const [deliveries, setDeliveries] = useState<Delivery[]>([]);
    const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);
    const [showDeliveryModal, setShowDeliveryModal] = useState(false);
    const [showCompletedModal, setShowCompletedModal] = useState(false);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otp, setOtp] = useState('');
    const [rating, setRating] = useState(4);
    const [apiStats, setApiStats] = useState<any>({
        totalOrders: 0,
        pendingOrders: 0,
        deliveredOrders: 0,
        cancelledOrders: 0,
        inProgressOrders: 0,
        newOrders: 0,
        totalRevenue: 0,
        earningsThisWeek: 0,
        weeklyEarningsChartData: []
    });
    const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [showJoinStoreModal, setShowJoinStoreModal] = useState(false);
    const [availableStores, setAvailableStores] = useState<any[]>([]);
    const [myRequests, setMyRequests] = useState<any[]>([]);

    useEffect(() => {
        if (showJoinStoreModal) {
            loadStoresAndRequests();
        }
    }, [showJoinStoreModal]);

    const loadStoresAndRequests = async () => {
        try {
            const [storesRes, requestsRes] = await Promise.all([
                storesAPI.getAll(),
                storeRequestsAPI.getMyRequests()
            ]);
            setAvailableStores(storesRes.data);
            setMyRequests(requestsRes.data);
        } catch (e) {
            toast.error('Failed to load stores');
        }
    };

    const handleJoinRequest = async (storeId: string) => {
        try {
            await storeRequestsAPI.create({ storeId });
            toast.success('Request sent!');
            loadStoresAndRequests();
        } catch (e: any) {
            toast.error(e.response?.data?.message || 'Failed to send request');
        }
    };

    // Live location tracking
    useEffect(() => {
        if ("geolocation" in navigator) {
            const watchId = navigator.geolocation.watchPosition(
                (position) => {
                    const newLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    setCurrentLocation(newLocation);
                    // Update backend with live location
                    usersAPI.updateLocation(newLocation.lat, newLocation.lng)
                        .catch(err => console.error("Failed to update location in backend:", err));
                },
                (error) => console.error("Error watching position:", error),
                { enableHighAccuracy: true }
            );
            return () => navigator.geolocation.clearWatch(watchId);
        }
    }, []);

    const displayStats = {
        newOrders: apiStats.newOrders || 0,
        inProgress: apiStats.inProgressOrders || 0,
        earningsThisWeek: apiStats.earningsThisWeek || 0,
        walletBalance: walletBalance || 0,
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/signup');
            return;
        }

        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                setUser(JSON.parse(userStr));
            } catch (e) {
                console.error('Error parsing user:', e);
            }
        }
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [walletRes, ordersRes, statsRes] = await Promise.all([
                walletAPI.getBalance().catch(() => ({ data: { balance: 0 } })),
                ordersAPI.getMyDeliveries().catch(() => ({ data: [] })),
                ordersAPI.getStats().catch(() => ({ data: {} }))
            ]);

            setWalletBalance(walletRes.data?.balance || 0);
            setApiStats(statsRes.data);

            // Update user in localStorage if wallet balance changed
            if (user && walletRes.data?.balance !== undefined) {
                const updatedUser = { ...user, wallet: { ...user.wallet, balance: walletRes.data.balance } };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                setUser(updatedUser);
            }

            // Transform orders to deliveries
            const orders = ordersRes.data || [];
            const todaysDeliveries: Delivery[] = orders
                .filter((o: any) => ['assigned', 'out_for_delivery', 'ready_for_pickup', 'confirmed', 'preparing'].includes(o.status))
                .slice(0, 6)
                .map((o: any) => ({
                    id: (o._id || o.id).toString().slice(-3),
                    orderId: o._id || o.id,
                    customer: o.userId?.fullName || o.userId?.name || 'Customer',
                    phone: o.userId?.phone || '+91 98765 43210',
                    address: typeof o.deliveryAddress === 'object' ? [
                        o.deliveryAddress.fullAddress || o.deliveryAddress.addressLine1,
                        o.deliveryAddress.city,
                        o.deliveryAddress.state,
                        o.deliveryAddress.pincode
                    ].filter(Boolean).join(', ') : (o.deliveryAddress || '123, Park St., Kolkata'),
                    items: o.items?.map((item: any) => ({
                        name: item.name || 'Product',
                        quantity: item.quantity || 1,
                        price: item.price || 0,
                    })) || [],
                    totalAmount: o.totalAmount || 0,
                    status: o.status,
                    distance: o.distance || `${(Math.random() * 2 + 0.3).toFixed(1)} km`,
                    eta: o.eta || `${Math.floor(Math.random() * 15) + 5} min`,
                    location: o.deliveryAddress?.location?.coordinates?.length === 2 ? {
                        lat: o.deliveryAddress.location.coordinates[1],
                        lng: o.deliveryAddress.location.coordinates[0]
                    } : undefined,
                    storeLocation: o.storeId?.address?.location?.coordinates?.length === 2 ? {
                        lat: o.storeId.address.location.coordinates[1],
                        lng: o.storeId.address.location.coordinates[0]
                    } : undefined,
                }));

            // Fetch real distance/ETA for assigned deliveries if possible using TravelTime
            const deliveriesWithRealData = await Promise.all(todaysDeliveries.map(async (d) => {
                let finalLoc = d.location;

                // Helper to try geocoding multiple variations
                const tryResolveLocation = async (addr: string): Promise<{ lat: number, lng: number } | null> => {
                    if (!addr) return null;
                    const cleanupStrategies = [
                        (a: string) => a, // Original
                        (a: string) => a.replace(/^(flat|floor|apartment|shop|room|plot|house|h\.?no\.?).*?,/i, '').trim(), // Remove common prefixes
                        (a: string) => a.split(',').slice(1).join(',').trim(), // Remove first part
                        (a: string) => { // Pincode + City
                            const pin = a.match(/\d{6}/);
                            return pin ? `${pin[0]}, In` : '';
                        }
                    ];

                    for (const strategy of cleanupStrategies) {
                        const cleanAddr = strategy(addr);
                        if (!cleanAddr || cleanAddr.length < 5) continue;
                        try {
                            const res = await geocode(cleanAddr);
                            if (res.features?.[0]?.geometry?.coordinates) {
                                return {
                                    lat: res.features[0].geometry.coordinates[1],
                                    lng: res.features[0].geometry.coordinates[0]
                                };
                            }
                        } catch (e) { /* continue */ }
                    }
                    return null;
                };

                // Auto-repair missing/invalid coords by geocoding
                if (!finalLoc || (finalLoc.lat === 0 && finalLoc.lng === 0)) {
                    if (d.address) {
                        const resolved = await tryResolveLocation(d.address);
                        if (resolved) finalLoc = resolved;
                    }
                }

                if (finalLoc && d.storeLocation) {
                    // Start routing from Agent Location if available (for accurate ETA), otherwise Store
                    const startPoint = currentLocation || d.storeLocation;

                    // Only fetch route if we have a valid destination (not 0,0)
                    if (finalLoc.lat !== 0 && finalLoc.lng !== 0) {
                        try {
                            const routeData = await getRoute({
                                origin: startPoint,
                                destination: finalLoc,
                                transportation: { type: 'driving' }
                            });

                            if (routeData.results?.[0]?.locations[0]?.properties[0]) {
                                const prop = routeData.results[0].locations[0].properties[0];
                                return {
                                    ...d,
                                    location: finalLoc,
                                    distance: `${(prop.distance / 1000).toFixed(1)} km`,
                                    eta: `${Math.round(prop.travel_time / 60)} min`
                                };
                            }
                        } catch (e) {
                            // console.error('Error fetching TravelTime routing data:', e);
                        }
                    } else if (d.storeLocation) {
                        // FALLBACK: If geocoding completely failed and left us with 0,0
                        // Use the Store Location just to keep the map sanely centered on *something*
                        // instead of the ocean.
                        return { ...d, location: d.storeLocation };
                    }
                }
                return { ...d, location: finalLoc || d.location };
            }));

            setDeliveries(deliveriesWithRealData);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStartDelivery = (delivery: Delivery) => {
        setSelectedDelivery(delivery);
        setShowDeliveryModal(true);
    };

    const handleCompleteDelivery = async () => {
        if (!selectedDelivery) return;

        // Geofencing Check using TravelTime: If agent is too far in time/distance, show warning
        if (selectedDelivery.location && currentLocation) {
            try {
                const routeData = await getRoute({
                    origin: currentLocation,
                    destination: selectedDelivery.location,
                    transportation: { type: 'driving' }
                });

                if (routeData.results?.[0]?.locations[0]?.properties[0]) {
                    const prop = routeData.results[0].locations[0].properties[0];
                    const travelTimeMins = prop.travel_time / 60;

                    if (travelTimeMins > 5) { // If more than 5 mins away
                        if (!window.confirm(`TravelTime estimates you are still about ${Math.round(travelTimeMins)} minutes (${(prop.distance / 1000).toFixed(1)}km) away. Are you sure you've reached the destination?`)) {
                            return;
                        }
                    }
                }
            } catch (e) {
                console.error('TravelTime geofence check failed, falling back to basic check', e);
            }
        }

        setShowDeliveryModal(false);
        setShowOtpModal(true);
    };

    const handleVerifyOtp = async () => {
        if (!selectedDelivery || !otp || otp.length !== 6) {
            toast.error('Please enter a valid 6-digit OTP');
            return;
        }

        try {
            const response = await ordersAPI.verifyOTP(selectedDelivery.orderId, otp);
            if (response.data.isValid) {
                setShowOtpModal(false);
                setOtp('');
                setShowCompletedModal(true);
                // Backend automatically sets status to delivered
            } else {
                toast.error('Invalid OTP. Please check with the customer.');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Verification failed');
        }
    };

    const handleSubmitDelivery = () => {
        setShowCompletedModal(false);
        setSelectedDelivery(null);
        toast.success('Delivery completed successfully!');
        fetchData();
    };

    useEffect(() => {
        if (selectedDelivery) {
            const updated = deliveries.find(d => d.id === selectedDelivery.id);
            // If the background repair updated the location (and it's different from what we have), sync it
            if (updated && JSON.stringify(updated.location) !== JSON.stringify(selectedDelivery.location)) {
                setSelectedDelivery(prev => prev ? ({ ...prev, ...updated }) : null);
                if ((updated.location?.lat !== 0 || updated.location?.lng !== 0)) {
                    toast.success("Location pinpointed!");
                }
            }
        }
    }, [deliveries, selectedDelivery]);

    const handleNavigate = async () => {
        if (!selectedDelivery) return;
        const { location, storeLocation, address } = selectedDelivery;

        let fromLat = currentLocation?.lat || storeLocation?.lat;
        let fromLng = currentLocation?.lng || storeLocation?.lng;
        let toLat = location?.lat;
        let toLng = location?.lng;

        // If coordinates are missing, try to geocode the address first to avoid TravelTime's buggy search page
        if ((!toLat || !toLng) && address) {
            try {
                toast.loading('Resolving address...', { id: 'nav-geocode' });
                const geocodeRes = await geocode(address);
                if (geocodeRes.features?.[0]) {
                    const coords = geocodeRes.features[0].geometry.coordinates;
                    toLat = coords[1];
                    toLng = coords[0];
                    toast.success('Address resolved!', { id: 'nav-geocode' });
                } else {
                    toast.error('Could not find address location.', { id: 'nav-geocode' });
                }
            } catch (err) {
                toast.error('Geocoding failed.', { id: 'nav-geocode' });
            }
        }

        if (fromLat && fromLng && toLat && toLng) {
            // TravelTime Routing Viewer URL (Direct to routing with coordinates)
            // Explicitly force driving mode
            const url = `https://app.traveltime.com/routing?frLat=${fromLat}&frLng=${fromLng}&toLat=${toLat}&toLng=${toLng}&mode=driving&transportation=driving`;
            console.log("Opening Navigation URL:", url);
            window.open(url, '_blank');
        } else if (address) {
            // FALLBACK: If coordinates are still missing after geocoding attempt, 
            // use Google Maps instead of the buggy TravelTime search page.
            // This ensures the driver actually gets a map instead of a crash.
            const fallbackUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
            window.open(fallbackUrl, '_blank');
            toast('Using Google Maps fallback (TravelTime could not resolve address)', { icon: '🗺️' });
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="animate-spin text-[#10b981]" size={48} />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                            Welcome back, {user?.name || 'John'}!
                        </h1>
                        <button
                            onClick={() => setShowJoinStoreModal(true)}
                            className="mt-2 text-sm text-[#10b981] font-medium flex items-center gap-1 hover:underline"
                        >
                            <Plus size={16} /> Join New Store
                        </button>
                    </div>
                    <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
                        <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Balance</span>
                        <span className="text-lg font-bold text-[#10b981]">₹{displayStats.walletBalance.toLocaleString()}</span>
                    </div>
                </div>

                {/* Stats Cards - Matching image */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <ShoppingCart className="text-purple-600" size={20} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">{displayStats.newOrders}</p>
                                <p className="text-sm text-gray-600">New Orders</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <Truck className="text-green-600" size={20} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">{displayStats.inProgress}</p>
                                <p className="text-sm text-gray-600">In Progress</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <DollarSign className="text-blue-600" size={20} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">
                                    ₹{displayStats.earningsThisWeek >= 1000 ? `${(displayStats.earningsThisWeek / 1000).toFixed(1)}K` : displayStats.earningsThisWeek}
                                </p>
                                <p className="text-sm text-gray-600">Earnings This Week</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <DollarSign className="text-orange-600" size={20} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">₹{displayStats.walletBalance.toLocaleString()}</p>
                                <p className="text-sm text-gray-600">Wallet Balance</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Today's Deliveries */}
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-gray-800">Today's Deliveries</h2>
                        <button className="text-sm text-[#10b981] hover:underline">View All</button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-sm text-gray-500 border-b">
                                    <th className="pb-3 font-medium">ID</th>
                                    <th className="pb-3 font-medium">Customer</th>
                                    <th className="pb-3 font-medium">Address</th>
                                    <th className="pb-3 font-medium">Items</th>
                                    <th className="pb-3 font-medium">Amount</th>
                                    <th className="pb-3 font-medium">Status</th>
                                    <th className="pb-3 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deliveries.map((delivery) => (
                                    <tr key={delivery.id} className="border-b border-gray-50 hover:bg-gray-50">
                                        <td className="py-4 text-sm font-medium text-[#10b981]">#{delivery.id}</td>
                                        <td className="py-4">
                                            <p className="text-sm font-medium text-gray-800">{delivery.customer}</p>
                                            <p className="text-xs text-gray-500">{delivery.phone}</p>
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 max-w-[200px] truncate">{delivery.address}</td>
                                        <td className="py-4 text-sm text-gray-600">{delivery.items.length} items</td>
                                        <td className="py-4 text-sm font-semibold text-gray-800">₹{delivery.totalAmount}</td>
                                        <td className="py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${delivery.status === 'OUT_FOR_DELIVERY' ? 'bg-orange-100 text-orange-800' :
                                                delivery.status === 'READY_FOR_PICKUP' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                {delivery.status === 'OUT_FOR_DELIVERY' ? 'In Transit' :
                                                    delivery.status === 'READY_FOR_PICKUP' ? 'Ready' : delivery.status}
                                            </span>
                                        </td>
                                        <td className="py-4">
                                            <button
                                                onClick={() => handleStartDelivery(delivery)}
                                                className="px-3 py-1.5 bg-[#10b981] text-white text-sm rounded-lg hover:bg-[#059669] transition-colors"
                                            >
                                                Start
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {deliveries.length === 0 && (
                        <div className="text-center py-8">
                            <Truck className="mx-auto text-gray-400 mb-4" size={48} />
                            <p className="text-gray-600">No deliveries assigned for today.</p>
                        </div>
                    )}
                </div>

                {/* Earnings Chart */}
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">This Week's Earnings</h2>
                    <div className="h-48 flex items-end justify-between gap-4">
                        {(apiStats.weeklyEarningsChartData && apiStats.weeklyEarningsChartData.length > 0) ? (
                            apiStats.weeklyEarningsChartData.map((day: any, i: number) => {
                                const maxVal = Math.max(...apiStats.weeklyEarningsChartData.map((d: any) => d.total), 1);
                                const dayName = new Date(day._id).toLocaleDateString('en-US', { weekday: 'short' });
                                return (
                                    <div key={i} className="flex-1 flex flex-col items-center">
                                        <div
                                            className="w-full bg-[#10b981] rounded-t-lg hover:bg-[#059669] transition-all cursor-pointer group relative"
                                            style={{ height: `${(day.total / maxVal) * 150}px` }}
                                        >
                                            <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                                ₹{day.total}
                                            </div>
                                        </div>
                                        <span className="text-xs font-semibold text-gray-500 mt-3">{dayName}</span>
                                    </div>
                                );
                            })
                        ) : (
                            [0, 0, 0, 0, 0, 0, 0].map((_, i) => (
                                <div key={i} className="flex-1 flex flex-col items-center">
                                    <div className="w-full bg-gray-100 rounded-t-lg h-2" />
                                    <span className="text-xs text-gray-400 mt-3">
                                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Delivery Modal - Matching image */}
                {showDeliveryModal && selectedDelivery && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
                            {/* Header */}
                            <div className="bg-gray-50 p-4 flex items-center justify-between">
                                <button onClick={() => setShowDeliveryModal(false)} className="text-gray-600">
                                    ←
                                </button>
                                <h2 className="font-semibold text-gray-800">Delivery Agent</h2>
                                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                            </div>

                            {/* Order Info */}
                            <div className="p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center space-x-2">
                                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">ID</span>
                                        <span className="font-semibold text-gray-800">#{selectedDelivery.id}</span>
                                    </div>
                                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs">In Minute</span>
                                </div>

                                <div className="mb-4">
                                    <p className="font-semibold text-gray-800 text-lg">{selectedDelivery.customer}</p>
                                    <p className="text-gray-600 text-sm flex items-center">
                                        <MapPin size={14} className="mr-1" />
                                        {selectedDelivery.address}
                                    </p>
                                </div>

                                {/* Items */}
                                <div className="mb-4">
                                    <p className="font-semibold text-gray-800 mb-2">{selectedDelivery.items.length} Items</p>
                                    <div className="flex items-center justify-between text-lg font-bold text-[#10b981]">
                                        <span></span>
                                        <span>₹{selectedDelivery.totalAmount}</span>
                                    </div>
                                </div>

                                <div className="space-y-2 mb-4">
                                    {selectedDelivery.items.map((item, i) => (
                                        <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100">
                                            <div className="flex items-center space-x-2">
                                                <span className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-xs">✓</span>
                                                <span className="text-sm text-gray-700">{item.name} - {item.quantity}</span>
                                            </div>
                                            <span className="text-sm font-medium text-gray-800">₹{item.price * item.quantity}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center justify-between py-2 border-t border-gray-200">
                                    <span className="font-semibold text-gray-700">Total</span>
                                    <span className="font-bold text-gray-800">Total ₹{selectedDelivery.totalAmount}</span>
                                </div>

                                {/* TravelTime Map Integration */}
                                <div className="h-64 bg-gray-100 rounded-lg mb-4 overflow-hidden relative border border-gray-200">
                                    <TravelTimeMap
                                        center={[
                                            currentLocation?.lat || selectedDelivery.storeLocation?.lat || 0,
                                            currentLocation?.lng || selectedDelivery.storeLocation?.lng || 0
                                        ]}
                                        origin={currentLocation || selectedDelivery.storeLocation}
                                        destination={selectedDelivery.location}
                                        showIsochrone={true}
                                        travelTimeLimit={900} // 15 mins reachable area
                                        onRouteUpdate={(dist, eta) => {
                                            // Sync live map calculation (Agent -> Customer) to the UI display
                                            if (selectedDelivery && (selectedDelivery.distance !== dist || selectedDelivery.eta !== eta)) {
                                                setSelectedDelivery(prev => prev ? ({ ...prev, distance: dist, eta: eta }) : null);
                                            }
                                        }}
                                    />
                                    <div className="absolute bottom-2 left-2 bg-white/90 px-3 py-1 rounded text-xs text-gray-700 shadow-md z-[1000] font-medium border border-gray-100 italic">
                                        Live: {selectedDelivery.eta} - {selectedDelivery.distance}
                                    </div>
                                </div>

                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-gray-600">{selectedDelivery.eta} - {selectedDelivery.distance}</span>
                                    <button
                                        onClick={handleNavigate}
                                        className="flex items-center space-x-1 bg-[#10b981] text-white px-4 py-2 rounded-lg hover:bg-[#059669] transition-colors"
                                    >
                                        <Navigation size={16} />
                                        <span>Navigate</span>
                                    </button>
                                </div>

                                {/* Slide to Deliver */}
                                <button
                                    onClick={handleCompleteDelivery}
                                    className="w-full bg-[#10b981] text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 hover:bg-[#059669] transition-colors"
                                >
                                    <span>Slide to Deliver</span>
                                    <ArrowRight size={20} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* OTP Verification Modal */}
                {showOtpModal && selectedDelivery && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center">
                            <h2 className="text-xl font-bold text-gray-800 mb-2">Verify Delivery</h2>
                            <p className="text-gray-500 mb-6 text-sm">Ask customer for the OTP to complete delivery</p>

                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                    setOtp(val);
                                }}
                                placeholder="• • • • • •"
                                className="w-full text-center text-3xl tracking-[0.5em] font-bold py-3 border-b-2 border-gray-200 focus:border-[#10b981] outline-none mb-8 text-gray-800"
                                autoFocus
                            />

                            <div className="flex gap-3">
                                <button
                                    onClick={() => { setShowOtpModal(false); setShowDeliveryModal(true); }}
                                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleVerifyOtp}
                                    disabled={otp.length !== 6}
                                    className="flex-1 py-3 bg-[#10b981] text-white rounded-xl font-semibold hover:bg-[#059669] disabled:bg-gray-300 transition-colors"
                                >
                                    Verify
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Completed Modal - Matching image */}
                {showCompletedModal && selectedDelivery && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
                            {/* Header */}
                            <div className="bg-gray-50 p-4 flex items-center justify-between">
                                <button onClick={() => setShowCompletedModal(false)} className="text-gray-600">
                                    ←
                                </button>
                                <h2 className="font-semibold text-gray-800">Delivery Agent</h2>
                                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                            </div>

                            <div className="p-6 text-center">
                                {/* Success Icon */}
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="text-green-500" size={32} />
                                </div>

                                <h2 className="text-xl font-bold text-gray-800 mb-2">Order Delivered!</h2>
                                <p className="text-gray-600 mb-6">#{selectedDelivery.id} has been successfully delivered.</p>

                                {/* Rating */}
                                <p className="text-gray-700 mb-2">How was the delivery experience?</p>
                                <div className="flex items-center justify-center space-x-2 mb-6">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            onClick={() => setRating(star)}
                                            className={`text-2xl ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
                                        >
                                            ★
                                        </button>
                                    ))}
                                </div>

                                {/* Note */}
                                <textarea
                                    placeholder="Add a note... (optional)"
                                    className="w-full p-3 border border-gray-300 rounded-lg mb-4 text-sm"
                                    rows={2}
                                />

                                {/* Upload Photo */}
                                <div className="mb-6">
                                    <p className="text-gray-700 mb-2">Upload Photo</p>
                                    <div className="flex items-center justify-center space-x-4">
                                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                                            <Camera className="text-gray-400" size={24} />
                                        </div>
                                        <button className="flex items-center space-x-2 text-[#10b981]">
                                            <Camera size={18} />
                                            <span>Upload Photo</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Submit */}
                                <button
                                    onClick={handleSubmitDelivery}
                                    className="w-full bg-[#10b981] text-white py-3 rounded-lg font-semibold hover:bg-[#059669] transition-colors"
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Join Store Modal */}
                {showJoinStoreModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                            <div className="bg-gray-50 p-4 flex items-center justify-between border-b">
                                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                                    <Store size={20} /> Join Store
                                </h2>
                                <button onClick={() => setShowJoinStoreModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
                            </div>

                            <div className="p-6 overflow-y-auto">
                                {/* My Requests Status */}
                                {myRequests.length > 0 && (
                                    <div className="mb-8">
                                        <h3 className="font-bold text-gray-700 mb-3">Your Requests</h3>
                                        <div className="space-y-3">
                                            {myRequests.map(req => (
                                                <div key={req._id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-100">
                                                    <div>
                                                        <p className="font-medium text-gray-800">{req.storeId?.name || 'Unknown Store'}</p>
                                                        <p className="text-xs text-gray-500">{new Date(req.createdAt).toLocaleDateString()}</p>
                                                    </div>
                                                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${req.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                        req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                                            'bg-yellow-100 text-yellow-700'
                                                        }`}>
                                                        {req.status}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <h3 className="font-bold text-gray-700 mb-3">Available Stores</h3>
                                <div className="grid gap-4">
                                    {availableStores.map(store => {
                                        const existingRequest = myRequests.find(r => r.storeId?._id === store._id || r.storeId === store._id);
                                        const isMember = user?.storeIds?.includes(store._id);

                                        return (
                                            <div key={store._id} className="flex items-center justify-between border p-4 rounded-xl hover:shadow-sm transition-all">
                                                <div>
                                                    <h4 className="font-bold text-gray-800">{store.name}</h4>
                                                    <p className="text-sm text-gray-500 flex items-center gap-1">
                                                        <MapPin size={14} /> {store.address?.city || 'No City'}
                                                    </p>
                                                </div>

                                                {isMember ? (
                                                    <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                                                        <CheckCircle size={16} /> Member
                                                    </span>
                                                ) : existingRequest ? (
                                                    <span className="text-sm font-medium text-yellow-600">
                                                        Request Pending
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleJoinRequest(store._id)}
                                                        className="bg-[#10b981] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#059669]"
                                                    >
                                                        Request to Join
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {availableStores.length === 0 && (
                                        <p className="text-center text-gray-500 py-4">No stores available to join.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default DeliveryAgentDashboard;
